import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { Notification, NotificationOptions } from './types';

const delayedNotificationsAtom = atomWithStorage<Notification[]>(
  'notifications',
  [],
  createJSONStorage(() => sessionStorage),
  {
    getOnInit: true,
  }
);

const immediateNotificationsAtom = atom<Notification[]>([]);

const setNotificationAtom = atom(null, (get, set, { type, message, isDismissable = true }: NotificationOptions) => {
  set(delayedNotificationsAtom, [
    ...get(delayedNotificationsAtom),
    { id: crypto.randomUUID(), type, message, isDismissable },
  ]);
});

const showNotificationAtom = atom(null, (get, set, { type, message, isDismissable = true }: NotificationOptions) => {
  set(immediateNotificationsAtom, [
    ...get(immediateNotificationsAtom),
    { id: crypto.randomUUID(), type, message, isDismissable },
  ]);
});

const flushNotificationsAtom = atom(null, (get, set) => {
  const delayed = get(delayedNotificationsAtom);
  const immediate = get(immediateNotificationsAtom);
  set(immediateNotificationsAtom, [...immediate, ...delayed]);
  set(delayedNotificationsAtom, []);
});

const dismissNotificationAtom = atom(null, (get, set, id: string) => {
  set(
    immediateNotificationsAtom,
    get(immediateNotificationsAtom).filter(notification => notification.id !== id)
  );
});

interface UseNotificationsHookResult {
  showNotification: (notification: NotificationOptions) => void;
  setNotification: (notification: NotificationOptions) => void;
  dismissNotification: (id: string) => void;
  notifications: Notification[];
  flushNotifications: () => void;
}

export function useNotifications(): UseNotificationsHookResult {
  const [notifications] = useAtom(immediateNotificationsAtom);
  const setNotification = useSetAtom(setNotificationAtom);
  const showNotification = useSetAtom(showNotificationAtom);
  const flushNotifications = useSetAtom(flushNotificationsAtom);
  const dismissNotification = useSetAtom(dismissNotificationAtom);

  return { notifications, showNotification, setNotification, dismissNotification, flushNotifications };
}
